import type { AxisKey } from './rig'

/**
 * Die Übersetzung — eine Zahl, eine Kurve oder eine Matrix.
 *
 * WARUM NICHT IMMER EINE ZAHL. Ein Planetensatz hat eine Übersetzung; sie steht
 * im Datenblatt und ändert sich nicht. Eine Kurbelschwinge hat KEINE: ihre
 * Übersetzung hängt am Gelenkwinkel, und wer sie als Mittelwert führt, rechnet
 * an beiden Enden des Bereichs falsch. Eine Parallelkinematik hat nicht einmal
 * eine je Achse: dort wirkt jeder Motor auf jede Achse, und die Zuordnung ist
 * eine Matrix.
 *
 * Bisher stand überall eine Zahl. Beim Knie war es 9,9 — ein Wert, der in der
 * Ausarbeitung P00 nirgends vorkommt. Dort steht: Planetenstufe 21,2 vor der
 * Kurbel, Kettenübersetzung zwischen 1,03 und 1,06 im Gehbereich, und daraus
 * „eine Gesamtübersetzung von 22". Die Kurve unten rechnet genau das aus der
 * Geometrie und ersetzt die Zahl.
 */

/* -------------------------------------------------------------------------
   Die Kurbelschwinge
   ------------------------------------------------------------------------- */

/**
 * Ein Viergelenk: Kurbel am Getriebeabtrieb, Schubstange, Hebel am Abtriebsglied.
 *
 * Alle Längen in Millimetern, alle Winkel in Grad.
 */
export interface Linkage {
  /** Kurbel am Getriebeabtrieb. */
  crank: number
  /** Schubstange. */
  rod: number
  /** Hebel am angetriebenen Glied. */
  rocker: number
  /** Lage des Kurbeldrehpunkts im Rahmen des tragenden Glieds. */
  pivot: { x: number; y: number }
  /**
   * Grundwinkel des Hebels gegen die Achse des angetriebenen Glieds.
   *
   * ACHTUNG, ZWEI KONVENTIONEN. P00 nennt −95° und misst gegen die Senkrechte
   * auf der Schienbeinachse; hier wird gegen die Achse selbst gemessen. Das sind
   * dieselben 90 Grad Unterschied, die man beim Übertragen einmal übersieht und
   * danach eine Kette hat, die bei 95 Grad einbricht statt bei 145.
   */
  base: number
  /** Dreht der Hebel mit dem Gelenkwinkel gleich- oder gegenläufig? */
  sense: 1 | -1
  /** Welcher der beiden Zusammenbauzustände. */
  branch: 1 | -1
}

/**
 * Die Kurbelschwinge des Knies nach P00.
 *
 * DIE FÜNF ZAHLEN SIND VERÖFFENTLICHT: Kurbel 38 mm am Getriebeabtrieb, 176 mm
 * über dem Knie; Schubstange 178 mm; Hebel am Schienbein 40 mm; Grundwinkel
 * −95°; Planetenstufe 21,2 davor.
 *
 * Die Gegenprobe steht im Test: die daraus gerechnete Kette liegt zwischen 0 und
 * 90 Grad bei 1,026 bis 1,059 — P00 nennt „zwischen 1,03 und 1,06" —, erreicht
 * bei 140 Grad 1,42 gegen dort genannte 1,39, und bricht bei 152 Grad ein gegen
 * dort genannte 145. Vier Kennwerte, vier Treffer; die letzten beiden auf ein
 * paar Prozent, weil die Längen nur auf den Millimeter angegeben sind.
 */
export const KNEE_LINKAGE: Linkage = {
  crank: 38,
  rod: 178,
  rocker: 40,
  pivot: { x: 0, y: 176 },
  base: -5,
  sense: 1,
  branch: -1,
}

/** Die Vorstufe vor der Kurbel — zweistufiger Planetensatz. */
export const KNEE_STAGE = 21.2

const DEG = Math.PI / 180

export interface LinkageTrack {
  /** Gelenkwinkel je Stützstelle, Grad. */
  angles: Float64Array
  /** Bis wohin abgetastet wurde, Grad. */
  to: number
  /** Übersetzung der Kette an dieser Stelle. */
  ratios: Float64Array
  /**
   * Wo die Kette einbricht — die Totlage, jenseits derer es keine Lösung mehr
   * gibt. `Infinity`, solange der abgetastete Bereich reicht.
   */
  breaks: number
}

/**
 * Fährt das Viergelenk einmal durch und merkt sich die Übersetzung.
 *
 * WARUM ALS BAHN UND NICHT ALS FORMEL JE WINKEL. Die Schliessbedingung hat zwei
 * Lösungen; welche gilt, entscheidet der Zusammenbau. Wer sie je Winkel einzeln
 * löst, springt an jeder Stelle, an der die beiden Lösungen einander nahe
 * kommen, unbemerkt auf die andere — und bekommt eine Übersetzung, die
 * plötzlich ihren Kehrwert annimmt. Die Bahn wird deshalb fortgesetzt: jeder
 * Schritt startet bei der Lösung des vorigen.
 */
export function linkageTrack(link: Linkage, to = 170, samples = 680): LinkageTrack {
  const angles = new Float64Array(samples + 1)
  /* Das Winkelraster steht unabhängig davon, wie weit die Kette trägt — sonst
     stünden hinter der Totlage Nullen, und die Abfrage rechnete mit ihnen. */
  for (let k = 0; k <= samples; k++) angles[k] = (k / samples) * to
  const ratios = new Float64Array(samples + 1).fill(NaN)
  const { crank: r, rod: l, rocker: h, pivot } = link
  const base = link.base * DEG

  /* Das Hebelende im Rahmen des tragenden Glieds. */
  const arm = (deg: number): [number, number] => {
    const t = link.sense * deg * DEG
    /* Achse des angetriebenen Glieds, bei null nach unten. */
    const dx = -Math.sin(t)
    const dy = -Math.cos(t)
    const c = Math.cos(base)
    const s = Math.sin(base)
    return [h * (dx * c - dy * s), h * (dx * s + dy * c)]
  }

  const close = (phi: number, deg: number): number => {
    const [qx, qy] = arm(deg)
    const px = pivot.x + r * Math.cos(phi)
    const py = pivot.y + r * Math.sin(phi)
    return (px - qx) ** 2 + (py - qy) ** 2 - l * l
  }
  const slope = (phi: number, deg: number): number => {
    const [qx, qy] = arm(deg)
    const px = pivot.x + r * Math.cos(phi)
    const py = pivot.y + r * Math.sin(phi)
    return -2 * (px - qx) * r * Math.sin(phi) + 2 * (py - qy) * r * Math.cos(phi)
  }

  /* Startlösung bei null, über den Kosinussatz. */
  const [q0x, q0y] = arm(0)
  const dx = q0x - pivot.x
  const dy = q0y - pivot.y
  const d = Math.hypot(dx, dy)
  const cos = (r * r + d * d - l * l) / (2 * r * d)
  if (!Number.isFinite(cos) || Math.abs(cos) > 1) {
    return { angles, to, ratios, breaks: 0 }
  }
  let phi = Math.atan2(dy, dx) + link.branch * Math.acos(cos)

  const crankAngles = new Float64Array(samples + 1).fill(NaN)
  let last = samples
  for (let k = 0; k <= samples; k++) {
    const deg = angles[k]
    let ok = true
    for (let it = 0; it < 60; it++) {
      const dphi = slope(phi, deg)
      /* Steigung null heisst Totlage: die Kurbel steht in der Verlängerung der
         Schubstange und lässt sich von dieser Seite nicht weiterdrehen. */
      if (Math.abs(dphi) < 1e-9) {
        ok = false
        break
      }
      const step = close(phi, deg) / dphi
      phi -= step
      if (Math.abs(step) < 1e-13) break
    }
    if (!ok || !Number.isFinite(phi) || Math.abs(close(phi, deg)) > 1e-6) {
      last = k - 1
      break
    }
    crankAngles[k] = phi
  }

  const step = to / samples
  for (let k = 1; k < last; k++) {
    if (!Number.isFinite(crankAngles[k - 1]) || !Number.isFinite(crankAngles[k + 1])) continue
    ratios[k] = Math.abs((crankAngles[k + 1] - crankAngles[k - 1]) / (2 * step * DEG))
  }
  /* Die Ränder aus dem Nachbarn — eine Ableitung braucht zwei Nachbarn, und
     ohne diese Zeile stünde am Anfang und Ende der Bahn eine Lücke. */
  if (last >= 2) {
    ratios[0] = ratios[1]
    ratios[last] = ratios[last - 1]
  }

  return { angles, to, ratios, breaks: last >= samples ? Infinity : (last / samples) * to }
}

/* Einmal je Viergelenk, nicht je Abfrage: die Bahn hängt nur an der Geometrie. */
const tracks = new WeakMap<Linkage, LinkageTrack>()

export function trackFor(link: Linkage): LinkageTrack {
  let track = tracks.get(link)
  if (!track) {
    track = linkageTrack(link)
    tracks.set(link, track)
  }
  return track
}

/**
 * Die Kettenübersetzung bei einem Gelenkwinkel.
 *
 * Jenseits der Totlage `NaN` — dort gibt es keine Stellung, und eine Zahl wäre
 * eine Behauptung über eine Kinematik, die es nicht gibt.
 */
export function linkageRatio(link: Linkage, deg: number): number {
  const track = trackFor(link)
  const n = track.angles.length - 1
  if (deg < 0 || deg > track.to) return NaN
  const x = (deg / track.to) * n
  const i = Math.min(n - 1, Math.floor(x))
  const f = x - i
  const a = track.ratios[i]
  const b = track.ratios[i + 1]
  if (!Number.isFinite(a)) return Number.isFinite(b) ? b : NaN
  if (!Number.isFinite(b)) return a
  return a + (b - a) * f
}

/* -------------------------------------------------------------------------
   Die drei Formen
   ------------------------------------------------------------------------- */

export type Ratio =
  /** Eine Zahl — Planetensatz, Riemen, Kegelrad, direkt. */
  | { kind: 'zahl'; value: number }
  /**
   * Eine Kurve über dem Gelenkwinkel — Kurbelschwinge.
   *
   * `stage` ist die feste Vorstufe vor der Kurbel; die Gesamtübersetzung ist ihr
   * Produkt mit der Kette.
   */
  | { kind: 'kurve'; stage: number; linkage: Linkage }
  /**
   * Eine Matrix — Parallelkinematik.
   *
   * `rows[i][j]` ist der Beitrag von Motor `j` zur Achse `i`. Eine Zuordnung
   * „ein Motor je Achse" gibt es dort nicht: beide Motoren wirken auf beide
   * Plattformwinkel, die Summe stellt die eine Achse, die Differenz die andere.
   */
  | { kind: 'matrix'; axes: AxisKey[]; rows: number[][]; nominal: number }

/**
 * Die Übersetzung bei einem Gelenkwinkel.
 *
 * Für Zahl und Matrix ist der Winkel ohne Belang — sie haben keine Abhängigkeit
 * davon. Bei der Matrix wird der Nennwert gereicht; welcher Motor welchen
 * Anteil trägt, beantwortet erst `ratio.rows`, und dafür braucht es die
 * Stellung beider Achsen und nicht einen Winkel.
 */
export function ratioAt(ratio: Ratio, deg = 0): number {
  if (ratio.kind === 'zahl') return ratio.value
  if (ratio.kind === 'matrix') return ratio.nominal
  const chain = linkageRatio(ratio.linkage, deg)
  return Number.isFinite(chain) ? ratio.stage * chain : NaN
}

export interface RatioSpan {
  min: number
  max: number
  mean: number
  /** Wo die Kette einbricht, falls im geprüften Bereich. */
  breaks: number
}

/**
 * Kleinster, grösster und mittlerer Wert über einem Winkelbereich.
 *
 * DER MITTELWERT IST DER NENNWERT, und deshalb steht er hier und nicht als
 * zweite Zahl daneben: eine Kurve, deren Mittelwert an einer anderen Stelle
 * gepflegt wird, hat zwei Wahrheiten, sobald jemand die Geometrie ändert.
 */
export function ratioSpan(ratio: Ratio, lo = 0, hi = 90, steps = 90): RatioSpan {
  if (ratio.kind !== 'kurve') {
    const v = ratioAt(ratio)
    return { min: v, max: v, mean: v, breaks: Infinity }
  }
  let min = Infinity
  let max = -Infinity
  let sum = 0
  let count = 0
  for (let k = 0; k <= steps; k++) {
    const deg = lo + ((hi - lo) * k) / steps
    const v = ratioAt(ratio, deg)
    if (!Number.isFinite(v)) continue
    min = Math.min(min, v)
    max = Math.max(max, v)
    sum += v
    count++
  }
  const breaks = trackFor(ratio.linkage).breaks
  if (count === 0) return { min: NaN, max: NaN, mean: NaN, breaks }
  return { min, max, mean: sum / count, breaks }
}

/** Wie die Übersetzung in einer Zeile steht. */
export function ratioLabel(ratio: Ratio, lo = 0, hi = 90): string {
  if (ratio.kind === 'zahl') return `${round(ratio.value)} : 1`
  if (ratio.kind === 'matrix') return `${round(ratio.nominal)} : 1, Matrix ${ratio.rows.length}×${ratio.rows[0].length}`
  const span = ratioSpan(ratio, lo, hi)
  if (!Number.isFinite(span.mean)) return '—'
  return `${round(span.min)} … ${round(span.max)} : 1`
}

/* Punkt und nicht Komma: die Seiten schreiben ihre Zahlen über `num` aus
   `sim/parts`, und zwei Schreibweisen nebeneinander in derselben Zeile liest
   man als Fehler. */
const round = (v: number): string => (Math.abs(v) >= 10 ? v.toFixed(1) : v.toFixed(2))

/* -------------------------------------------------------------------------
   Gen5 — die Stangenantriebe des Stands V0_24
   ------------------------------------------------------------------------- */

/**
 * Die Kurbelschwinge des Gen5-Knies.
 *
 * AUS DEN CAD-PUNKTEN DES STANDS in die Sagittalebene des Oberschenkels
 * projiziert (x nach vorn, y nach oben, Ursprung Knieachse B5): Motorachse
 * (122,8, −0,8, −314,2) liegt 26,5 mm vor und 179,1 mm über dem Knie
 * (131,1, 25,7, −493,3); Kurbel 72,3; Stange 181,1; Hebel 51,6 am
 * Unterschenkel, in der Nullpose 33 mm vor und 39,8 mm über dem Knie — das
 * sind die 140,3° Grundwinkel gegen die Schienbeinachse. Die Kurbel steht in
 * der Nullpose vor und über der Motorachse (26°), daher `branch: 1`.
 *
 * DIE GEGENPROBE STEHT IM TEST: der Stand nennt für Kurbel/Knie 0,36
 * gestreckt, 0,71 bei 60–70° und 0,32 bei 140°. Die Kette aus dieser Geometrie
 * liefert 0,365, 0,714 bei 64° und 0,32 bei 140. Davor eine Taumelstufe 46,26
 * — die Gesamtübersetzung erreicht damit 33.
 */
export const GEN5_KNEE_LINKAGE: Linkage = {
  crank: 72.3,
  rod: 181.1,
  rocker: 51.6,
  pivot: { x: 26.5, y: 179.1 },
  base: 140.3,
  sense: 1,
  branch: 1,
}

/** Die Getriebestufe vor der Kniekurbel — 2× FMK08523, i 46,26. */
export const GEN5_KNEE_STAGE = 46.26

/**
 * Die Bizeps-Zugstange des Gen5-Ellbogens.
 *
 * Gleiche Projektion, Ursprung Ellbogenachse A4 (241, 79, 125): Motorachse
 * (223,8, 72,8, 251,4) 6,2 mm vor und 126,4 mm über dem Ellbogen; Kurbel 45,
 * in der Nullpose 30° nach vorn-unten; Stange 137,8; Hebel 30, 75° unter vorn
 * (6,3 mm vor und 29 mm unter dem Ellbogen → 12,3° gegen die Unterarmachse).
 * Beugen schwenkt den Unterarm nach vorn, daher `sense: −1`.
 *
 * Gegenprobe: der Stand nennt 0,33 gestreckt, 0,71 bei 90°, 0,57 bei 130°;
 * die Kette liefert 0,33, 0,72 und 0,61. Davor i 50 (FMK07019).
 */
export const GEN5_ELBOW_LINKAGE: Linkage = {
  crank: 45,
  rod: 137.8,
  rocker: 30,
  pivot: { x: 6.2, y: 126.4 },
  base: 12.3,
  sense: -1,
  branch: 1,
}

/** Die Getriebestufe vor der Bizepskurbel — FMK07019, i 50. */
export const GEN5_ELBOW_STAGE = 50
